import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { EllipsisVertical } from "lucide-react"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button } from "@/components/ui/primitives"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { PickerTab } from "./reference-picker-types"

const TAB_BUTTON_CLASS = "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
const MORE_BTN_RESERVE_PX = 36
const TABLIST_CHROME_PX = 6
const ROW_GAP_PX = 8

export type ReferencePickerUploadRender = (ctx: { compact: boolean }) => ReactNode

export type ReferencePickerTabsProps = {
  tabs: PickerTab[]
  pickerTab: PickerTab
  onTabChange: (tab: PickerTab) => void
  upload?: ReferencePickerUploadRender
}

function tabLabel(tab: PickerTab, t: ReturnType<typeof useTranslation>["t"]): string {
  if (tab === "character") return t("canvas.panel.pickCharacter")
  if (tab === "location") return t("canvas.panel.pickLocation")
  if (tab === "prop") return t("canvas.panel.pickProp")
  return t("canvas.panel.pickPrimitive")
}

function fitTabCount(widths: number[], available: number, tabCount: number): number {
  const fit = (avail: number) => {
    let used = TABLIST_CHROME_PX
    let count = 0
    for (const width of widths) {
      if (used + width > avail + 0.5) break
      used += width
      count += 1
    }
    return count
  }
  let count = fit(available)
  if (count < tabCount) count = fit(available - MORE_BTN_RESERVE_PX)
  return Math.max(tabCount > 0 ? 1 : 0, Math.min(tabCount, count))
}

export function ReferencePickerTabs({ tabs, pickerTab, onTabChange, upload }: ReferencePickerTabsProps) {
  const { t } = useTranslation("workspace")
  const outerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const actionsFullMeasureRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(tabs.length)
  const [compact, setCompact] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const labels = useMemo(
    () => Object.fromEntries(tabs.map((tab) => [tab, tabLabel(tab, t)])) as Record<PickerTab, string>,
    [t, tabs],
  )

  useEffect(() => {
    const outer = outerRef.current
    const measure = measureRef.current
    if (!outer || !measure || typeof ResizeObserver === "undefined") return

    const recompute = () => {
      const chips = Array.from(measure.children) as HTMLElement[]
      const widths = chips.map((chip) => chip.offsetWidth)
      const outerW = outer.clientWidth
      const fullActionsW = actionsFullMeasureRef.current?.offsetWidth ?? 0
      const fullGap = fullActionsW > 0 ? ROW_GAP_PX : 0
      const countIfFull = fitTabCount(widths, outerW - fullActionsW - fullGap, tabs.length)
      const nextCompact = Boolean(upload) && countIfFull < tabs.length

      const actualActionsW = actionsRef.current?.offsetWidth ?? 0
      const actualGap = actualActionsW > 0 ? ROW_GAP_PX : 0
      const countActual = fitTabCount(widths, outerW - actualActionsW - actualGap, tabs.length)

      setCompact(nextCompact)
      setVisibleCount(countActual)
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(outer)
    if (actionsRef.current) observer.observe(actionsRef.current)
    if (actionsFullMeasureRef.current) observer.observe(actionsFullMeasureRef.current)
    return () => observer.disconnect()
    // upload — render-prop; size tracked via ResizeObserver on measure nodes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, tabs, Boolean(upload)])

  const { visibleTabs, overflowTabs } = useMemo(() => {
    const visible = tabs.slice(0, visibleCount)
    const overflow = tabs.slice(visibleCount)
    if (overflow.includes(pickerTab) && visible.length > 0) {
      const last = visible[visible.length - 1]
      return {
        visibleTabs: [...visible.slice(0, -1), pickerTab],
        overflowTabs: [last, ...overflow.filter((tab) => tab !== pickerTab)],
      }
    }
    return { visibleTabs: visible, overflowTabs: overflow }
  }, [pickerTab, tabs, visibleCount])

  useEffect(() => {
    if (overflowTabs.length === 0 && menuOpen) setMenuOpen(false)
  }, [overflowTabs.length, menuOpen])

  const overflowKey = overflowTabs.join(",")
  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menuOpen,
    direction: "down",
    align: "left",
    anchorRef: moreButtonRef,
    menuRef,
    rootRef: outerRef,
    positionKey: overflowKey,
    estimatedMenuWidthPx: 192,
    resolveWidth: () => ({
      minWidth: "12rem",
      width: "max-content",
      maxWidth: "min(24rem, calc(100vw - 16px))",
    }),
    onOutsideClick: () => setMenuOpen(false),
  })

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false)
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [menuOpen])

  const renderTab = (tab: PickerTab) => (
    <button
      key={tab}
      type="button"
      role="tab"
      aria-selected={pickerTab === tab}
      onClick={() => onTabChange(tab)}
      className={cn(
        TAB_BUTTON_CLASS,
        pickerTab === tab ? UI_ACCENT_FILLED : "text-ui-text-secondary hover:text-ui-text-primary",
      )}
    >
      {labels[tab]}
    </button>
  )

  const overflowMenu =
    menuOpen && menuStyle ? (
      <div
        ref={menuRef}
        role="menu"
        style={menuStyle}
        className="overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
      >
        {overflowTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="menuitem"
            className={cn(
              "flex w-full items-center px-3 py-2 text-left text-xs hover:bg-ui-surface-sunken",
              pickerTab === tab ? "text-ui-accent-text" : "text-ui-text-primary",
            )}
            onClick={() => {
              setMenuOpen(false)
              onTabChange(tab)
            }}
          >
            {labels[tab]}
          </button>
        ))}
      </div>
    ) : null

  return (
    <div ref={outerRef} className="relative flex min-w-0 flex-nowrap items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <div className="flex min-w-0 flex-nowrap items-center gap-2">
          <div
            className="inline-flex min-w-0 flex-nowrap rounded-lg border border-ui-border bg-ui-surface-sunken p-0.5"
            role="tablist"
            aria-label={t("canvas.referencesModal.title")}
          >
            {visibleTabs.map(renderTab)}
          </div>
          {overflowTabs.length > 0 ? (
            <Button
              ref={moreButtonRef}
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 w-7 shrink-0 px-0"
              aria-label={t("canvas.referencesModal.moreTabsAria")}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <EllipsisVertical className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 -z-10 flex w-max opacity-0"
        >
          {tabs.map((tab) => (
            <span key={tab} className={cn(TAB_BUTTON_CLASS, "shrink-0")}>
              {labels[tab]}
            </span>
          ))}
        </div>
      </div>
      {upload ? (
        <>
          <div ref={actionsRef} className="flex shrink-0 flex-nowrap items-center gap-2">
            {upload({ compact })}
          </div>
          <div
            ref={actionsFullMeasureRef}
            aria-hidden
            inert
            className="pointer-events-none absolute top-0 left-0 -z-10 flex w-max flex-nowrap items-center gap-2 opacity-0"
          >
            {upload({ compact: false })}
          </div>
        </>
      ) : null}
      {overflowMenu && portalRoot ? createPortal(overflowMenu, portalRoot) : null}
    </div>
  )
}
